import { differenceInCalendarMonths, getTime, isDate } from 'date-fns';

import { nanoid } from "nanoid";
import numeral from "numeral";
import { BulkItem, CartItem, CompanyTokens, OrderItem, PaymentMethod, PaymentType, UserTokens } from "../core/global";

export const getCompanyPaymentOptions = (item: CompanyTokens, isTokenOrder: boolean) => {
  const items: PaymentMethod[] = []
  const {name, tokens_value, role, super_subscriber, members_pre_authorised, vat_exempt} = item
  if (!role || role === 'external-user' || (!members_pre_authorised && role !== 'admin')) {
    items.push({
      id: nanoid(),
      type: 'request-approval',
      title: `Request ${name} to approve purchase`,
      config: {
        ...item
      }
    })
  } else {
    if (super_subscriber === true) {
      if (role === 'member') {
        if (members_pre_authorised && !isTokenOrder) {
          items.push({
            id: nanoid(),
            type: 'subscription',
            title: `Acquire with ${name} subscription`,
            config: {
              ...item
            }
          })
        } else {
          items.push({
            id: nanoid(),
            type: 'request-approval',
            title: `Request ${name} to approve purchase`,
            config: {
              ...item
            }
          })
        }
      } else if (!isTokenOrder) {
        items.push({
          id: nanoid(),
          type: 'subscription',
          title: `Acquire with ${name} subscription`,
          config: {
            ...item
          }
        })
      }
    } else {

      if (import.meta.env.VITE_ENABLE_TOKENS === "true") {
        // The company is not super subscriber
        if (tokens_value && tokens_value > 0 && !isTokenOrder) {
          items.push({
            id: nanoid(),
            type: 'company-tokens',
            title: `Pay with ${name} tokens (${numeral(tokens_value).format("0,0")} available)`,
            config: {
              ...item
            }
          })
        }
      }

      // if (import.meta.env.VITE_ENABLE_STRIPE === "true") {
      //   items.push({
      //     id: nanoid(),
      //     type: 'company-stripe',
      //     title: `Pay by card (Stripe) for ${name}${vat_exempt ? `- V.A.T. exempt` : ``}`,
      //     config: {
      //       ...item
      //     }
      //   })
      // }

      if (import.meta.env.VITE_ENABLE_PO === "true") {
        items.push({
          id: nanoid(),
          type: 'company-po',
          title: `Pay by Purchase Order (PO) for ${name}${vat_exempt ? ` - V.A.T. exempt` : ``}`,
          config: {
            ...item
          }
        })
      } 

    } 
  }
  if (import.meta.env.VITE_ENABLE_STRIPE === "true") {
    items.push({
      id: nanoid(),
      type: 'company-stripe',
      title: `Pay by card (Stripe) for ${name}${vat_exempt ? `  - V.A.T. exempt` : ``}`,
      config: {
        ...item
      }
    })
  }
  return items
}

export function getPaymentOptions({
  userTokens,
  userCompanyTokens,
  isTokenOrder,
}: {
  userTokens: UserTokens[] | null,
  userCompanyTokens: CompanyTokens[] | null,
  isTokenOrder: boolean,
}): PaymentMethod[] {
  const items: PaymentMethod[] = []
  const companyMethods: PaymentMethod[] = []

  if (userCompanyTokens && Array.isArray(userCompanyTokens) && userCompanyTokens.length > 0) {
    userCompanyTokens.forEach( item => {
      const options = getCompanyPaymentOptions(item, isTokenOrder)
      companyMethods.push(...options)
    });
  }
  const foundAcquire: PaymentMethod[] = companyMethods.filter( ({title}) => {
    return title.indexOf("Acquire") !== -1
  })
  items.push(...foundAcquire)

  if (Array.isArray(userTokens) && userTokens.length > 0 && !isTokenOrder) {
    const totalTokens =  userTokens.reduce((acc, item) => {
      const {tokens_value} = item
      if (tokens_value && !isNaN(tokens_value)) {
        return acc + Number(tokens_value)
      }
      return acc
    }, 0)

    const totalDiscount = userTokens.reduce((acc, item) => {
      const {discount_applied_percent} = item
      if (discount_applied_percent && !isNaN(discount_applied_percent)) {
        return acc + Number(discount_applied_percent)
      }
      return acc
    }, 0)/userTokens.length

    if (import.meta.env.VITE_ENABLE_TOKENS === "true") {
      items.push({
        id: nanoid(),
        type: 'tokens',
        title: `Pay with user tokens (${numeral(totalTokens).format("0,0")} available)`,
        config: {
          tokens: totalTokens,
          discount_applied_percent: totalDiscount,
        }
      })
    }
  }

  if (import.meta.env.VITE_ENABLE_STRIPE === "true") {
    items.push({
      id: nanoid(),
      type: 'stripe',
      title: `Pay by card (Stripe)`,
      config: {
        vat_exempt: false,
        company_id: null,
        tokens: userTokens
      }
    })
  }
  
  if (import.meta.env.VITE_ENABLE_PO === "true") {
    items.push({
      id: nanoid(),
      type: 'po',
      title: `Pay by Purchase Order (PO)`,
      config: {
        tokens: userTokens
      }
    })
  }

  const notFoundAcquire: PaymentMethod[] = companyMethods.filter( ({title}) => {
    return title.indexOf("Acquire") === -1
  })
  items.push(...notFoundAcquire)

  return [...items]
}


export const estimateCost = (startDate: Date | undefined, endDate: Date | undefined, costPerYear: number | null) => {
  if (startDate && endDate && isDate(startDate) && isDate(endDate)) {
    const months = differenceInCalendarMonths(endDate, startDate)
    const years = Math.abs(Math.ceil(months/12))
    const token_cost = Number(costPerYear || 0)
    return years * token_cost
  }
  return null
}

// HACK: to implement for Tokens purchases, I guess this can even stay
export const getCostFromTokens = (item: BulkItem) => {
  const {tokens, discount: discountPercent} = item
  const token_cost = Number(import.meta.env.VITE_TOKEN_COST_PER_YEAR)
  const subtotal = tokens*token_cost
  const discount = subtotal/100*discountPercent
  const total =  subtotal - discount
  return {
    total,
    subtotal,
    discount,
  }
}

export const getBasketCost = (data: CartItem[] | OrderItem[], isVatExempt = false, paymentType: PaymentType | null) => {
  const vatPercentage = isVatExempt ? 0 : 20
  
  const subtotal = data.map(({cost}) => cost).reduce((prev, next) => prev + next, 0)
  let fullDataSetCounter = 0
  data.forEach(({cost, start_date, end_date}) => {
    const isFreeDataSet = Number(cost || 0) === 0
    const months = differenceInCalendarMonths(new Date(end_date), new Date(start_date))
    const years = Math.abs(Math.ceil(months/12))
    const fullDataSet = years >= 43
    if (fullDataSet && !isFreeDataSet) {
      fullDataSetCounter += 1
    }
  })

  const discountPercentage = (paymentType === "card" || paymentType === "po") && fullDataSetCounter >= 3 ? 12 : 0
  const discountValue = (subtotal/100)*discountPercentage
  const discountTokens = Math.ceil((subtotal/100)/Number(import.meta.env.VITE_TOKEN_COST_PER_YEAR)*discountPercentage)
  const subtotalAfterDiscount = subtotal - discountValue
  const total = subtotalAfterDiscount + (subtotalAfterDiscount/100)*vatPercentage
  const vatValue =  (subtotalAfterDiscount/100)*vatPercentage
  const subtotalTokens = Math.ceil(subtotal/Number(import.meta.env.VITE_TOKEN_COST_PER_YEAR))
  const totalTokens = subtotalTokens - discountTokens
  return {
    vatValue, 
    vatPercentage, 
    subtotal, 
    subtotalTokens,
    total,
    totalTokens,
    discountPercentage,
    discountTokens,
    discountValue,
  }
}

export const getBasketCostForTokens = (data: number[], isVatExempt = false) => {
  const vatPercentage = isVatExempt ? 0 : 20
  const subtotal = data.reduce((prev, next) => prev + next, 0)
  const subtotalAfterDiscount = subtotal
  const total = subtotalAfterDiscount + (subtotalAfterDiscount/100)*vatPercentage
  const vatValue =  (subtotalAfterDiscount/100)*vatPercentage
  return {
    vatValue, 
    vatPercentage, 
    subtotal, 
    total,
  }
}


interface ItemExistsInArrayProps {
  data: any[]
  lat: number | null
  lon: number | null
  start_date: number | null
  end_date: number | null
  model: string | null
  version: string | null | number
}

export const itemExistsInArray = ({data, lat, lon, start_date, end_date, model, version}: ItemExistsInArrayProps) => {
  return data?.filter((projectItem) => {
    
    const {
      lat: _lat, 
      lon: _lon, 
      start_date: _start_date, 
      end_date: _end_date, 
      model: _model,
      version: _version
    } = projectItem

    const startDate = getTime(new Date(_start_date))
    const endDate = getTime(new Date(_end_date))

      return (
      lat === _lat &&
      lon === _lon &&
      model === _model &&
      startDate === start_date && 
      endDate === end_date &&
      version === _version
    )
  });
}